import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Observable, throwError } from 'rxjs';
import { environment } from '../../../../environments/environment.development';
import { STORAGE_ENUM } from '../../../auth/enums/storage.enum';
import { AuthService } from '../../../auth/services/auth/auth.service';
import { SessionService } from '../../../auth/services/session/session.service';
import { LocalStorageService } from '../../../shared/services/local-storage/local-storage.service';

@Injectable({
  providedIn: 'root'
})
export class TasksService {
  private _http = inject(HttpClient);
  private _authService = inject(AuthService);
  private _sessionService = inject(SessionService);
  private _localStorage = inject(LocalStorageService);

  private _baseUrl = `${environment.apiUrl}/administrative`;

  getTasks(filters: any): Observable<any> {
    const token = this._localStorage.get(STORAGE_ENUM.TOKEN);

    const headers = {
      Authorization: `Bearer ${token}`
    };

    if (!token) {
      return throwError(() => new Error('Error getting tasks'));
    }

    const isMainCustomer = this._sessionService.Customer.is_main;
    const customerId = this._sessionService.Customer.id;
    let tasksUrl = `${this._baseUrl}/tasks/tasks/?customer=${customerId}`;

    if (isMainCustomer) {
      tasksUrl += `&customer__main_customer=${customerId}`;
    }

    if (filters.search) {
      tasksUrl += `&search=${filters.search}`;
    }

    if (filters.page) {
      tasksUrl += `&page=${filters.page}`;
    }

    if (filters.page_size) {
      tasksUrl += `&page_size=${filters.page_size}`;
    }

    if (filters.status) {
      tasksUrl += `&status=${filters.status}`;
    }

    if (filters.task_type) {
      tasksUrl += `&task_type=${filters.task_type}`;
    }

    if (filters.action_plan) {
      tasksUrl += `&action_plan=${filters.action_plan}`;
    }

    return this._http.get<any>(tasksUrl, {
      headers
    });
  }

  createTask(
    title: string,
    description: string,
    initial_date: string,
    final_date: string,
    areas: string[],
    task_type: string,
  ): Observable<any> {
    const token = this._localStorage.get(STORAGE_ENUM.TOKEN);

    const headers = {
      Authorization: `Bearer ${token}`
    };

    if (!token) {
      return throwError(() => new Error('Error al guardar la tarea'));
    }

    const customerId = this._sessionService.Customer.id;
    const jobCenterId = this._sessionService.Customer.job_center.id;

    const body = {
      customer: customerId,
      job_center: jobCenterId,
      action_plan_id: null,
      assigned_task: 0,
      automatic_task: 0,
      description,
      event: null,
      initial_date,
      final_date,
      incidense: 0,
      nesting_area_id: areas,
      suggestion: "",
      task_type,
      title,
      user: null,
    };

    return this._http.post<any>(`${this._baseUrl}/tasks/tasks/`, body, {
      headers
    });
  }

  getNestingAreas(query?: string): Observable<any[]> {
    const token = this._localStorage.get(STORAGE_ENUM.TOKEN);

    const headers = {
      Authorization: `Bearer ${token}`
    };

    const isMainCustomer = this._sessionService.Customer.is_main;
    const customerId = this._sessionService.Customer.id;
    let url = `${this._baseUrl}/pest-control/nesting-areas/?customer=${customerId}&page=1&page_size=10`;

    if (isMainCustomer) {
      url += `&customer__main_customer=${customerId}`;
    }

    if (query) {
      url += `&search=${query}`;
    }

    return this._http.get<any[]>(url, {
      headers
    });
  }

  getTaskTypes(query?: string): Observable<any[]> {
    const token = this._localStorage.get(STORAGE_ENUM.TOKEN);

    const headers = {
      Authorization: `Bearer ${token}`
    };

    let url = `${this._baseUrl}/catalogs/task-types/?page=1&page_size=10`;

    if (query) {
      url += `&search=${query}`;
    }

    return this._http.get<any[]>(url, {
      headers
    });
  }

  getStatusTasks(): Observable<any[]> {
    const token = this._localStorage.get(STORAGE_ENUM.TOKEN);

    const headers = {
      Authorization: `Bearer ${token}`
    };

    return this._http.get<any[]>(`${this._baseUrl}/catalogs/statuses/?module=TS`, {
      headers
    });
  }

  patchTask(data: any, taskId: string): Observable<any> {
    const token = this._localStorage.get(STORAGE_ENUM.TOKEN);

    const headers = {
      Authorization: `Bearer ${token}`
    };

    if (!token) {
      return throwError(() => new Error('Error al actualizar la tarea'));
    }

    return this._http.patch<any>(`${this._baseUrl}/tasks/tasks/${taskId}/`, data, {
      headers
    });
  }

  getTaskPhotos(taskId: string): Observable<any[]> {
    const token = this._localStorage.get(STORAGE_ENUM.TOKEN);

    const headers = {
      Authorization: `Bearer ${token}`
    };

    let url = `${this._baseUrl}/tasks/task-photo/?task=${taskId}`;

    return this._http.get<any[]>(url, {
      headers
    });
  }

  createTaskPhoto(data: FormData): Observable<any> {
    const token = this._localStorage.get(STORAGE_ENUM.TOKEN);

    const headers = {
      Authorization: `Bearer ${token}`
    };

    return this._http.post<any>(`${this._baseUrl}/tasks/task-photo/`, data, {
      headers
    });
  }

  deleteTaskPhoto(photoId: string): Observable<any> {
    const token = this._localStorage.get(STORAGE_ENUM.TOKEN);

    const headers = {
      Authorization: `Bearer ${token}`
    };

    return this._http.delete<any>(`${this._baseUrl}/tasks/task-photo/${photoId}/`, {
      headers
    });
  }

  getTaskHistory(taskId: string, page: number = 1, pageSize: number = 5): Observable<any> {
    const token = this._localStorage.get(STORAGE_ENUM.TOKEN);

    const headers = {
      Authorization: `Bearer ${token}`
    };

    let url = `${this._baseUrl}/tasks/task_history/?task=${taskId}&page=${page}&page_size=${pageSize}`;

    return this._http.get<any>(url, {
      headers
    });
  }

  createTaskHistory(data: { task: string; comments: string }): Observable<any> {
    const token = this._localStorage.get(STORAGE_ENUM.TOKEN);

    const headers = {
      Authorization: `Bearer ${token}`
    };

    return this._http.post<any>(`${this._baseUrl}/tasks/task_history/`, data, {
      headers
    });
  }

  getTaskHistoryPhotos(taskHistoryId: string): Observable<any[]> {
    const token = this._localStorage.get(STORAGE_ENUM.TOKEN);

    const headers = {
      Authorization: `Bearer ${token}`
    };

    let url = `${this._baseUrl}/tasks/task_history_photo/?taskHistory=${taskHistoryId}`;

    return this._http.get<any[]>(url, {
      headers
    });
  }

  createTaskHistoryPhoto(data: FormData): Observable<any> {
    const token = this._localStorage.get(STORAGE_ENUM.TOKEN);

    const headers = {
      Authorization: `Bearer ${token}`
    };

    return this._http.post<any>(`${this._baseUrl}/tasks/task_history_photo/`, data, {
      headers
    });
  }

  deleteTaskHistory(historyId: string): Observable<any> {
    const token = this._localStorage.get(STORAGE_ENUM.TOKEN);

    const headers = {
      Authorization: `Bearer ${token}`
    };

    return this._http.delete<any>(`${this._baseUrl}/tasks/task_history/${historyId}/`, {
      headers
    });
  }

  deleteTaskHistoryPhoto(photoId: string): Observable<any> {
    const token = this._localStorage.get(STORAGE_ENUM.TOKEN);

    const headers = {
      Authorization: `Bearer ${token}`
    };

    return this._http.delete<any>(`${this._baseUrl}/tasks/task_history_photo/${photoId}/`, {
      headers
    });
  }
}
